import React from 'react';
import {
  Box,
  CssBaseline,
  GlobalStyles,
  Link,
  Paper,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import { slateInkTheme, slateInkTokens } from '@src/theme/slateInkTheme';
import { TroveWordmark, TroveMark } from './TroveLogo';

/*
 * /login renders inside <Admin>, which supplies the Slate Ink theme; /sso is
 * mounted outside <Admin>, so it would otherwise fall back to MUI defaults.
 * Applying the theme here keeps both auth screens on Slate Ink regardless of
 * where they mount. (Nesting the same theme under <Admin> for /login is a
 * harmless no-op.)
 */
const authTheme = createTheme(slateInkTheme);

/*
 * Shared scaffold for the auth screens (/login and /sso), styled to the Slate
 * Ink theme (src/theme/slateInkTheme.ts). Split-screen: dark slate brand panel
 * (with a slow "circularity rings" motif — a nod to recommerce) and a light
 * canvas panel holding the auth card. The brand panel collapses below the `md`
 * breakpoint; a dark wordmark appears above the card instead.
 *
 * Each screen supplies its own heading, sub-copy, and form body (children);
 * the brand panel, card shell, and footer are shared so the two screens stay
 * visually aligned.
 */

// Slate Ink tokens that have no palette slot (glows, tints on dark ink).
const RUST = slateInkTokens.accent;
const RUST_BRIGHT = slateInkTokens.accentBright; // headline accent — better contrast on dark ink
const INK_TEXT = slateInkTokens.appbarText;
const INK_MUTED = slateInkTokens.inkMuted;

const pageKeyframes = (
  <GlobalStyles
    styles={{
      '@keyframes troveFadeUp': {
        from: { opacity: 0, transform: 'translateY(14px)' },
        to: { opacity: 1, transform: 'none' },
      },
      '@keyframes troveSpin': {
        to: { transform: 'rotate(360deg)' },
      },
      // Covers both the class-based ring animation and the fadeUp animations
      // applied via sx props (which carry no shared class).
      '@media (prefers-reduced-motion: reduce)': {
        '*': { animation: 'none !important' },
      },
    }}
  />
);

export const fadeUp = (delay: number) => ({
  animation: `troveFadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s both`,
});

/** Concentric rings bleeding off the bottom-right of the brand panel. */
const CircularityRings = () => (
  <Box
    aria-hidden
    sx={{
      position: 'absolute',
      width: 620,
      height: 620,
      bottom: -230,
      right: -180,
      pointerEvents: 'none',
    }}
  >
    {/* soft rust glow behind the rings */}
    <Box
      sx={{
        position: 'absolute',
        inset: 60,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(194,90,32,0.28) 0%, rgba(194,90,32,0) 62%)',
      }}
    />
    <Box sx={{ position: 'absolute', inset: 130, borderRadius: '50%', border: '1px solid rgba(238,241,245,0.10)' }} />
    <Box sx={{ position: 'absolute', inset: 210, borderRadius: '50%', border: '1px solid rgba(238,241,245,0.07)' }} />
    {/* rotating dashed ring with an orbiting rust dot */}
    <Box
      sx={{
        position: 'absolute',
        inset: 40,
        borderRadius: '50%',
        border: '1.5px dashed rgba(194,90,32,0.45)',
        animation: 'troveSpin 90s linear infinite',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -6,
          left: '50%',
          width: 11,
          height: 11,
          borderRadius: '50%',
          backgroundColor: RUST,
          boxShadow: '0 0 14px rgba(194,90,32,0.9)',
        },
      }}
    />
  </Box>
);

const BrandPanel = () => (
  <Box
    sx={{
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '46%',
      maxWidth: 680,
      p: { md: 6, lg: 8 },
      position: 'relative',
      overflow: 'hidden',
      bgcolor: 'secondary.main', // Slate Ink appbar near-black
      color: INK_TEXT,
      // layered atmosphere: cool sheen top-right, faint rust warmth low-left
      backgroundImage: `
        radial-gradient(90rem 60rem at 110% -20%, rgba(98,131,175,0.14), transparent 55%),
        radial-gradient(70rem 50rem at -30% 120%, rgba(194,90,32,0.16), transparent 55%)
      `,
    }}
  >
    {/* fine blueprint grid, fading toward the center */}
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `
          linear-gradient(rgba(238,241,245,0.035) 1px, transparent 1px),
          linear-gradient(90deg, rgba(238,241,245,0.035) 1px, transparent 1px)
        `,
        backgroundSize: '48px 48px',
        maskImage: 'radial-gradient(120% 120% at 0% 0%, black 30%, transparent 75%)',
      }}
    />
    <CircularityRings />

    <Box sx={{ position: 'relative', ...fadeUp(0.05) }}>
      <TroveWordmark height={20} color={INK_TEXT} />
    </Box>

    <Box sx={{ position: 'relative', maxWidth: 460 }}>
      <Typography
        sx={{
          ...fadeUp(0.12),
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: RUST_BRIGHT,
          mb: 2.5,
        }}
      >
        Partner Dashboard
      </Typography>
      <Typography
        component="h2"
        sx={{
          ...fadeUp(0.2),
          fontSize: { md: 38, lg: 44 },
          fontWeight: 800,
          lineHeight: 1.12,
          letterSpacing: '-0.025em',
          mb: 2.5,
        }}
      >
        Powering returns, resale, and clearance for the world&apos;s
        {' '}
        <Box component="span" sx={{ color: RUST_BRIGHT }}>
          most&#8209;loved
        </Box>
        {' '}
        brands.
      </Typography>
      <Typography sx={{ ...fadeUp(0.28), fontSize: 16, lineHeight: 1.65, color: INK_MUTED }}>
        Manage and track your program — all in one place.
      </Typography>
    </Box>

    <Box
      sx={{
        position: 'relative',
        ...fadeUp(0.36),
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        fontSize: 13,
        color: INK_MUTED,
      }}
    >
      {['Returns', 'Resale', 'Clearance'].map((label, i) => (
        <React.Fragment key={label}>
          {i > 0 && (
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'rgba(194,90,32,0.8)' }} />
          )}
          <span>{label}</span>
        </React.Fragment>
      ))}
    </Box>
  </Box>
);

const Footer = () => (
  <Typography
    variant="caption"
    sx={{ ...fadeUp(0.3), mt: 3.5, color: 'text.secondary', letterSpacing: '0.01em' }}
  >
    ©
    {' '}
    {new Date().getFullYear()}
    {' '}
    Trove Recommerce ·
    {' '}
    <Link href="https://trove.com/privacy-policy/" target="_blank" rel="noopener" color="inherit" underline="hover">
      Privacy
    </Link>
    {' '}
    ·
    {' '}
    <Link href="https://trove.com/terms-of-service/" target="_blank" rel="noopener" color="inherit" underline="hover">
      Terms
    </Link>
  </Typography>
);

interface AuthLayoutProps {
  heading: React.ReactNode;
  subcopy: React.ReactNode;
  children: React.ReactNode;
}

/** Split-screen shell shared by /login and /sso. */
export const AuthLayout = ({ heading, subcopy, children }: AuthLayoutProps) => (
  <ThemeProvider theme={authTheme}>
    <CssBaseline />
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      {pageKeyframes}
      <BrandPanel />

      <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2.5, sm: 6 },
        position: 'relative',
        // faint sheen so the canvas doesn't read flat
        backgroundImage: 'radial-gradient(60rem 40rem at 85% 0%, rgba(255,255,255,0.75), transparent 60%)',
      }}
    >
        {/* compact brand header when the panel is hidden */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4, color: 'text.primary', ...fadeUp(0.05) }}>
          <TroveWordmark height={18} />
        </Box>

        <Paper
        elevation={1}
        sx={{
          ...fadeUp(0.18),
          width: '100%',
          maxWidth: 424,
          borderRadius: 2,
          p: { xs: 3.5, sm: 5 },
        }}
      >
          <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: slateInkTokens.accentSoft,
            color: slateInkTokens.accentText,
            mb: 3,
          }}
        >
            <TroveMark height={19} />
          </Box>

          <Typography
          component="h1"
          sx={{ fontSize: 25, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2, mb: 1 }}
        >
            {heading}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14.5, mb: 3.5 }}>
            {subcopy}
          </Typography>

          {children}
        </Paper>

        <Footer />
      </Box>
    </Box>
  </ThemeProvider>
);

export default AuthLayout;
